export enum TenderStatuses {
  DRAFT = "draft",
  READY_FOR_BIDS = "ready_for_bids",
  BIDS_REQUESTED = "bids_requested",
  BIDDING_CLOSED = "bidding_closed",
  AWARDED_TO_OPERATOR = "awarded_to_operator",
  BIDDING_EXPIRED = "bidding_expired",
  CANCELLED = "cancelled",
}

export enum SettlementMethods {
  PER_GUEST = "per_guest",
  PER_VEHICLE = "per_vehicle",
  PER_VEHICLE_AVERAGE = "per_vehicle_average",
}

export enum ReservedFields {
  RACK_RATESHEET = "rack_ratesheet",
  NET_RATESHEET = "net_ratesheet",
  CALCULATED_NET_RATESHEET = "calculated_net_ratesheet",
  OVERRIDDEN_NET_RATESHEET = "overridden_net_ratesheet",
  SELECTED_NET_RATESHEET_FIELD = "selected_net_ratesheet_field",
  OPERATOR_FEE_RATESHEET = "operator_fee_ratesheet",
  HEADSET_FEE_RATESHEET = "headset_fee_ratesheet",
  TARGET_ADULTS = "target_adults",
  TARGET_CHILDREN = "target_children",
  TARGET_SALES = "target_sales",
  TARGET_MARGIN = "target_margin",
  TARGET_MINIMUM_DEPARTURE_PAX = "target_minimum_departure_pax",
  TARGET_MAXIMUM_DEPARTURE_PAX = "target_maximum_departure_pax",
  TARGET_MAXIMUM_CALL_PAX = "target_maximum_call_pax",
  TARGET_DEPARTURES = "target_departures",
  TARGET_MORNING_DEPARTURES = "target_morning_departures",
  TARGET_AFTERNOON_DEPARTURES = "target_afternoon_departures",
  TOUR_STOPS = "tour_stops",
  TOUR_IMAGES = "tour_images",
  ATTACHMENTS = "attachments",
  LOGISTICAL_COMPONENTS = "logistical_components",
  VENUE_COMPONENTS = "venue_components",
  REFRESHMENT_COMPONENTS = "refreshment_components",
  RESTAURANT_COMPONENTS = "restaurant_components",
  ENTERTAINMENT_COMPONENTS = "entertainment_components",
  GUIDE_COMPONENTS = "guide_components",
  HEADSET_COMPONENTS = "headset_components",
  OVERHEAD_CATEGORIES = "overhead_components",
  SETTLEMENT_METHOD = "settlement_method",
  EXTRA_FEES = "extra_fees",
  VEHICLES = "vehicles",
  TOUR_TYPE = "tour_type",
  TOUR_DESTINATION = "tour_destination",
  ACTIVITY_TYPE = "activity_type",
  ACTIVITY_LEVEL = "activity_level",
  ADA_COMPLIANT = "ada_compliant",
  TOUR_DESCRIPTION = "tour_description",
  SHORT_TOUR_DESCRIPTION = "short_tour_description",
  TOUR_NOTES = "tour_notes",
  TOP_THREE_REASONS = "top_three_reasons",
  CANCELLATION_POLICY_DESCRIPTION = "cancellation_policy_description",
  UNAVAILABLE_DAYS = "unavailable_days",
  OTHER_RELEVANT_UNAVAILABLE_INFORMATION = "other_relevant_information_on_days_and_times_the_tour_is_not_available",
  MINIMUM_AGE = "minimum_age",
  MAXIMUM_AGE = "maximum_age",
  MINIMUM_HEIGHT = "minimum_height",
  MAXIMUM_HEIGHT = "maximum_height",
  MINIMUM_WEIGHT = "minimum_weight",
  MAXIMUM_WEIGHT = "maximum_weight",
  MINIMUM_DRINKING_AGE = "minimum_drinking_age",
  MINIMUM_DRIVING_AGE = "minimum_driving_age",
  MEDICAL_CONDITION_RESTRICTIONS = "medical_condition_restrictions",
  WHEELCHAIR_SUITABLE = "wheelchair_suitable",
  BLIND_SUITABLE = "blind_suitable",
  DEAF_SUITABLE = "deaf_suitable",
  WALKING_DIFFICULTY_SUITABLE = "walking_difficulty_suitable",
  PREGNANCY_SUITABLE = "pregnancy_suitable",
  INSTRUCTORS_PROVIDED = "instructors_provided",
  CHANGING_FACILITIES_PROVIDED = "changing_facilities_provided",
  STORAGE_LOCKERS_PROVIDED = "storage_lockers_provided",
  TOWELS_PROVIDED = "towels_provided",
  WAIVER_FORMS_REQUIRED = "waiver_forms_required",
  INCLUDED_EQUIPMENT = "included_equipment",
  GUEST_GUIDANCE = "guest_guidance",
  WEBSITE_TOUR_NAME = "website_tour_name",
  OVERVIEW = "overview",
  HIGHLIGHTS = "highlights",
  WHAT_TO_BRING = "what_to_bring",
  WHAT_TO_WEAR = "what_to_wear",
  IMPORTANT_NOTES = "important_notes",
  WEBSITE_CANCELLATION_POLICY = "website_cancellation_policy",
  GROUND_OPERATOR = "ground_operator",
  EVENING_EXCURSION = "evening_excursion",
  START_LOCATION = "start_location",
  TOUR_DURATION = "tour_duration",
  TOUR_LANGUAGE = "tour_language",
  BUY_CURRENCY_CODE = "buy_currency_code",
  SELL_CURRENCY_CODE = "sell_currency_code",
  MEETING_LOCATION = "meeting_location",
  MEETING_TIME_BUFFER_ANCHORED = "meeting_time_buffer_anchored",
  MEETING_TIME_BUFFER_DOCKED = "meeting_time_buffer_docked",
  TIME_OF_DAY = "time_of_day",
}

// Cruise-line-scoped fields hidden outright (rather than placeholder-texted)
// on an operator tour idea (SHO-TSK-967). Meeting Location is the only field
// in this set: it has no meaningful "not yet set" placeholder state, so it
// stays hidden entirely while an operator holds the idea. Every other
// cruise-line-scoped field (Collaboration::DocumentSchemas'
// TOUR_PRODUCT_CRUISE_LINE_SPECIFIC_FIELDS and
// TOUR_PRODUCT_CRUISE_LINE_SPECIFIC_COMPONENT_TYPE_FIELDS) instead shows
// CRUISE_LINE_FIELD_PLACEHOLDER via ReferencedValueSelect's emptyMessage.
export const CruiseLineScopedFields: ReadonlySet<string> = new Set([
  ReservedFields.MEETING_LOCATION,
])

// Placeholder shown in place of a cruise-line-scoped field's value on an
// operator tour idea, wherever the field itself stays visible (SHO-TSK-967).
export const CRUISE_LINE_FIELD_PLACEHOLDER =
  "Cruise line-specific value. Edits possible during contracting."

export enum ReservedSections {
  TOUR_DETAILS = "tour_details",
  DESCRIPTION = "description",
  TOUR_STOPS = "tour_stops",
  TOUR_IMAGES = "tour_images",
  ATTACHMENTS = "attachments",
  LOGISTICAL_COMPONENTS = ReservedFields.LOGISTICAL_COMPONENTS,
  VENUE_COMPONENTS = ReservedFields.VENUE_COMPONENTS,
  REFRESHMENT_COMPONENTS = ReservedFields.REFRESHMENT_COMPONENTS,
  RESTAURANT_COMPONENTS = ReservedFields.RESTAURANT_COMPONENTS,
  ENTERTAINMENT_COMPONENTS = ReservedFields.ENTERTAINMENT_COMPONENTS,
  GUIDE_COMPONENTS = ReservedFields.GUIDE_COMPONENTS,
  HEADSET_COMPONENTS = ReservedFields.HEADSET_COMPONENTS,
  EXTRA_FEES = "extra_fees",
  ADDITIONAL_DETAILS = "additional_details",
  TOUR_COST = "tour_cost",
  SETTLEMENT = "settlement",
  FORECASTS = "forecasts",
}

export enum TourLocation {
  START_LOCATION = "start_location",
  TOUR_DURATION = "tour_duration",
}

export enum ReferencedValueTypes {
  ACTIVITY_LEVEL = "activityLevel",
  ACTIVITY_TYPE = "activityType",
  TOUR_TYPE = "tourType",
  VEHICLE_TYPE = "vehicleType",
  STOP_TYPE = "stopType",
  GUIDE_TYPE = "guideType",
  VENUE_TYPE = "venueType",
  REFRESHMENT_TYPE = "refreshmentType",
  ENTERTAINMENT_TYPE = "entertainmentType",
  WEBSITE_CANCELLATION_POLICY = "websiteCancellationPolicy",
  MEETING_LOCATION = "meetingLocation",
}
